import React, { useState, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { AuthProvider, msalInstance } from "./auth/AuthProvider";
import { Toaster } from "./components/ui/sonner";
import ProtectedRoute from "./components/ProtectedRoute";
import Navbar from "./components/Navbar";
import Login from "./components/Login";
import EventsList from "./pages/EventsList";
import CreateEvent from "./pages/CreateEvent";
import AdminPanel from "./pages/AdminPanel";
import GemaManagement from "./pages/GemaManagement";
import ScreenShow from "./pages/ScreenShow";
import PublicSetlist from "./pages/PublicSetlist";
import { apiService } from "./services/api";
import "./App.css";

const AuthCallback = () => {
    const { instance, inProgress } = useMsal();
    const isAuthenticated = useIsAuthenticated();
    const [handled, setHandled] = useState(false);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const [redirectTarget, setRedirectTarget] = useState(null);
    
    useEffect(() => {
        let isMounted = true;
        
        instance.handleRedirectPromise()
            .then((response) => {
                if (!isMounted) return;
                
                if (response?.account) {
                    instance.setActiveAccount(response.account);
                }
            })
            .catch((err) => {
                if (isMounted) {
                    setError(err);
                }
            })
            .finally(() => {
                if (isMounted) {
                    setHandled(true);
                }
            });
        
        return () => {
            isMounted = false;
        };
    }, [instance]);
    
    useEffect(() => {
        if (handled && !error && window.location.hash) {
            window.history.replaceState(null, document.title, window.location.pathname);
        }
    }, [handled, error]);
    
    useEffect(() => {
        if (!handled || error || !isAuthenticated) {
            return;
        }
        const storedTarget = localStorage.getItem("postLoginRedirect");
        if (storedTarget) {
            localStorage.removeItem("postLoginRedirect");
            setRedirectTarget(storedTarget);
        } else {
            setRedirectTarget("/");
        }
    }, [handled, error, isAuthenticated]);

    useEffect(() => {
        if (redirectTarget) {
            navigate(redirectTarget, { replace: true });
        }
    }, [redirectTarget, navigate]);

    if (isAuthenticated) {
        return redirectTarget ? null : <Navigate to="/" replace />;
    }
    
    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
                <div className="text-white text-center space-y-4">
                    <p className="text-lg">Die Anmeldung konnte nicht abgeschlossen werden.</p>
                    <p className="text-sm text-white/80">{error.message}</p>
                    <a
                        href="/login"
                        className="inline-flex items-center justify-center rounded-lg bg-white/10 px-4 py-2 text-sm font-semibold transition hover:bg-white/20"
                    >
                        Zurück zum Login
                    </a>
                </div>
            </div>
        );
    }
    
    if (!handled || inProgress !== InteractionStatus.None) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
                <div className="text-white text-center">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white mb-4"></div>
                    <p className="text-lg">Anmeldung wird abgeschlossen...</p>
                </div>
            </div>
        );
    }
    
    return <Navigate to="/login" replace />;
};

function AppContent() {
    const isAuthenticated = useIsAuthenticated();
    const { accounts } = useMsal();
    const location = useLocation();
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [userLoading, setUserLoading] = useState(false);
    const [contactSetupOpen, setContactSetupOpen] = useState(false);
    const [contactEmail, setContactEmail] = useState("");
    const [contactSaving, setContactSaving] = useState(false);
    const [contactSetupDismissed, setContactSetupDismissed] = useState(false);
    const [contactPromptMarking, setContactPromptMarking] = useState(false);
    const previousOpenByQueryRef = useRef(false);
    
    useEffect(() => {
        if (!isAuthenticated) {
            setUser(null);
            setUserLoading(false);
            return;
        }

        if (accounts.length === 0) {
            setUserLoading(true);
            return;
        }

        const fetchUserProfile = async () => {
            try {
                setUserLoading(true);

                const activeAccount = msalInstance.getActiveAccount();
                const primaryAccount = accounts[0];

                if (!activeAccount && primaryAccount) {
                    msalInstance.setActiveAccount(primaryAccount);
                }

                const profile = await apiService.getUserProfile();
                setUser(profile);
            } catch (error) {
                console.error("Error fetching user profile:", error);
            } finally {
                setUserLoading(false);
            }
        };
        
        fetchUserProfile();
    }, [isAuthenticated, accounts]);

    useEffect(() => {
        if (!isAuthenticated || userLoading || !user) {
            return;
        }
        const query = new URLSearchParams(location.search);
        const openByQuery = query.get("openContactSetup") === "1";
        const justRequestedByQuery = openByQuery && !previousOpenByQueryRef.current;

        if (justRequestedByQuery) {
            setContactEmail(user.notifications_email || user.email || "");
            setContactSetupDismissed(false);
            setContactSetupOpen(true);
            previousOpenByQueryRef.current = openByQuery;
            return;
        }

        if (!openByQuery && !user.profile_setup_prompted && !contactSetupDismissed) {
            setContactEmail(user.notifications_email || user.email || "");
            setContactSetupOpen(true);
        }

        previousOpenByQueryRef.current = openByQuery;
    }, [isAuthenticated, userLoading, user, location.search, contactSetupDismissed]);

    useEffect(() => {
        if (!isAuthenticated || !user || contactPromptMarking) {
            return;
        }
        const query = new URLSearchParams(location.search);
        const openByQuery = query.get("openContactSetup") === "1";
        if (openByQuery || user.profile_setup_prompted) {
            return;
        }
        setContactPromptMarking(true);
        setUser((prev) => prev ? ({ ...prev, profile_setup_prompted: true }) : prev);
        apiService.updateUserContactSettings({
            profile_setup_prompted: true,
        }).catch((error) => {
            console.error("Error marking contact setup as prompted:", error);
        }).finally(() => {
            setContactPromptMarking(false);
        });
    }, [isAuthenticated, user, location.search, contactPromptMarking]);

    const clearContactSetupQuery = () => {
        const query = new URLSearchParams(location.search);
        if (query.get("openContactSetup") !== "1") {
            return;
        }
        query.delete("openContactSetup");
        const nextSearch = query.toString();
        navigate(
            {
                pathname: location.pathname,
                search: nextSearch ? `?${nextSearch}` : "",
            },
            { replace: true }
        );
    };

    const closeContactSetup = () => {
        setContactSetupOpen(false);
        setContactSetupDismissed(true);
        clearContactSetupQuery();
    };

    const handleSaveContactSettings = async () => {
        try {
            setContactSaving(true);
            const trimmed = (contactEmail || "").trim();
            const updated = await apiService.updateUserContactSettings({
                notifications_email: trimmed || null,
                profile_setup_completed: true,
            });
            setUser((prev) => ({
                ...(prev || {}),
                notifications_email: updated.notifications_email || null,
                profile_setup_prompted: !!updated.profile_setup_prompted,
                profile_setup_completed: !!updated.profile_setup_completed,
            }));
            closeContactSetup();
        } catch (error) {
            console.error("Error updating contact settings:", error);
        } finally {
            setContactSaving(false);
        }
    };

    const handleSkipContactSetup = async () => {
        try {
            setContactSaving(true);
            const updated = await apiService.updateUserContactSettings({
                notifications_email: null,
                profile_setup_prompted: true,
            });
            setUser((prev) => ({
                ...(prev || {}),
                notifications_email: updated.notifications_email || null,
                profile_setup_prompted: !!updated.profile_setup_prompted,
                profile_setup_completed: !!updated.profile_setup_completed,
            }));
            closeContactSetup();
        } catch (error) {
            console.error("Error skipping contact setup:", error);
        } finally {
            setContactSaving(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
            {(isAuthenticated || location.pathname.startsWith("/setlist/")) && <Navbar user={user} />}
            <main className={isAuthenticated ? "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" : ""}>
                <Routes>
                    <Route path="/login" element={<Login />} />
                    <Route path="/auth" element={<AuthCallback />} />
                    <Route path="/screen" element={<ScreenShow />} />
                    <Route path="/setlist/:token" element={<PublicSetlist />} />
                    <Route
                        path="/"
                        element={
                            <ProtectedRoute>
                                <EventsList user={user} />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/my-events"
                        element={
                            <ProtectedRoute>
                                <EventsList user={user} view="mine" />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/create"
                        element={
                            <ProtectedRoute>
                                <CreateEvent user={user} />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin"
                        element={
                            <ProtectedRoute>
                                {userLoading || (isAuthenticated && !user) ? (
                                    <div className="flex justify-center items-center py-12">
                                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-600"></div>
                                    </div>
                                ) : user?.role === "admin" ? (
                                    <AdminPanel user={user} />
                                ) : (
                                    <Navigate to="/" replace />
                                )}
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/gema"
                        element={
                            <ProtectedRoute>
                                {userLoading || (isAuthenticated && !user) ? (
                                    <div className="flex justify-center items-center py-12">
                                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-600"></div>
                                    </div>
                                ) : user?.role === "admin" ? (
                                    <GemaManagement />
                                ) : (
                                    <Navigate to="/" replace />
                                )}
                            </ProtectedRoute>
                        }
                    />
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </main>
            <Toaster position="top-right" richColors />
            {contactSetupOpen && isAuthenticated && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 px-4">
                    <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl">
                        <div className="flex items-start justify-between gap-3">
                            <h3 className="text-xl font-bold text-gray-900">Kontakt-Setup</h3>
                            <button
                                type="button"
                                onClick={closeContactSetup}
                                className="rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                                aria-label="Dialog schließen"
                            >
                                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <p className="mt-2 text-sm text-gray-600">
                            Hinterlege eine alternative E-Mail für Erinnerungen (z. B. Setlist, Kennzahlen, Fristen).
                        </p>
                        <label className="mt-4 block text-sm font-medium text-gray-700">
                            Alternative E-Mail (optional)
                        </label>
                        <input
                            type="email"
                            value={contactEmail}
                            onChange={(eventValue) => setContactEmail(eventValue.target.value)}
                            placeholder={user?.email || "name@beispiel.de"}
                            className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500"
                        />
                        <div className="mt-5 flex flex-wrap justify-end gap-2">
                            <button
                                type="button"
                                onClick={handleSkipContactSetup}
                                disabled={contactSaving}
                                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 disabled:opacity-60"
                            >
                                Überspringen
                            </button>
                            <button
                                type="button"
                                onClick={handleSaveContactSettings}
                                disabled={contactSaving}
                                className="rounded-lg bg-purple-600 px-4 py-2 text-sm font-semibold text-white hover:bg-purple-700 disabled:opacity-60"
                            >
                                {contactSaving ? "Speichern..." : "Speichern"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function App() {
    return (
        <AuthProvider>
            <BrowserRouter>
                <AppContent />
            </BrowserRouter>
        </AuthProvider>
    );
}

export default App;
